import axiosConfig from './axiosConfig';
import { arcDashArray, arcMarkerPosition, SMALL_ARC_LEN } from '../components/DPR/onePageReport/arcGauge';
import { displayExpectedPipeLength } from '../utils/sowExpectedLength';
import { readSowOdDisplay, readSowWtDisplay } from '../utils/dprSectionReportHeader';

/** Peel API wrapper layers: { status, displayMessage, data: { … } }. */
export function unwrapOnePageApiPayload(raw) {
  let payload = raw;
  if (!payload || typeof payload !== 'object') return null;

  for (let depth = 0; depth < 5; depth += 1) {
    const inner = payload.data;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;

    const payloadHasContent =
      payload.dprLogId != null || payload.header != null || payload.bundle != null;
    if (payloadHasContent) break;

    const innerHasContent =
      inner.dprLogId != null || inner.header != null || inner.bundle != null;
    const innerIsStatusWrapper =
      (inner.status != null || inner.displayMessage != null) && !innerHasContent;

    if (innerHasContent) {
      payload = inner;
      break;
    }

    if (innerIsStatusWrapper || payload.status != null || payload.displayMessage != null) {
      payload = inner;
      continue;
    }

    break;
  }

  return payload;
}

function hasDashboardSections(payload) {
  if (!payload || typeof payload !== 'object') return false;
  return Boolean(
    payload.header
      || payload.progress?.pipeProgPct != null
      || payload.meta?.projectNumber
      || payload.bundle?.pipeProgPct != null
      || payload.bundle?.metaProjectNumber
  );
}

function pick(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (v != null && v !== '') return v;
  }
  return undefined;
}

/** Parse "Today 10" / "Today: 10" from PDF template `nums` field. */
function parseTodayFromNums(nums) {
  if (nums == null || nums === '') return undefined;
  const match = String(nums).trim().match(/today\s*:?\s*([\d.]+)/i);
  return match ? match[1] : undefined;
}

/**
 * Normalize left-column weekly-limit consumable gauge (matches PDF Handlebars cons-mod).
 */
export function normalizeConsumableGaugeItem(item) {
  if (!item || typeof item !== 'object') return item;

  const title = pick(item.title, item.name);
  const consumptionToday = pick(
    item.consumptionToday,
    item.todayUsed,
    item.today,
    item.usedToday,
    parseTodayFromNums(item.nums)
  );
  const weekUsed = pick(item.gaugeHeroVal, item.weekUsed, item.week, item.weeklyUsed);
  const weeklyLimit = pick(item.weeklyLimitQty, item.weekLimit, item.weeklyLimit);
  const heroNum = parseFloat(weekUsed ?? item.gaugeHeroVal);
  const limitNum = parseFloat(weeklyLimit);
  const scaleMaxRaw = pick(item.scaleMax, item.gaugeScaleMax);
  const scaleMaxNum = scaleMaxRaw != null
    ? parseFloat(scaleMaxRaw)
    : Number.isFinite(limitNum) && limitNum > 0
      ? limitNum
      : Number.isFinite(heroNum) && heroNum > 0
        ? Math.ceil(heroNum * 2)
        : 30;

  let arcPct = parseFloat(item.gaugePct ?? item.percent);
  if (!Number.isFinite(arcPct)) {
    if (Number.isFinite(heroNum) && Number.isFinite(limitNum) && limitNum > 0) {
      arcPct = (heroNum / limitNum) * 100;
    } else if (Number.isFinite(heroNum) && scaleMaxNum > 0) {
      arcPct = (heroNum / scaleMaxNum) * 100;
    } else {
      arcPct = 0;
    }
  }

  const arc = arcDashArray(arcPct, SMALL_ARC_LEN);
  const consUsedArcDash = arc.dash;
  const consArcLen = arc.len;

  const showLimitMarker = item.showLimitMarker !== false
    && (Number.isFinite(limitNum) || item.limitMarkerX != null);
  let limitMarkerX = item.limitMarkerX;
  let limitMarkerY = item.limitMarkerY;
  if (showLimitMarker && limitMarkerX == null && Number.isFinite(limitNum) && scaleMaxNum > 0) {
    const limitPct = (limitNum / scaleMaxNum) * 100;
    const pos = arcMarkerPosition(limitPct);
    limitMarkerX = pos.x;
    limitMarkerY = pos.y;
  }

  const gaugeHeroVal = weekUsed ?? item.gaugeHeroVal ?? (Number.isFinite(heroNum) ? heroNum : 0);

  const badgeClass = item.badgeClass ?? 'ok';
  const barColorByBadge = badgeClass === 'warn'
    ? '#f59e0b'
    : badgeClass === 'low'
      ? '#dc2626'
      : '#22c55e';

  return {
    ...item,
    title,
    name: pick(item.name, title),
    consumptionToday,
    gaugeHeroVal,
    gaugePct: Number.isFinite(arcPct) ? arcPct.toFixed(2) : '0',
    weeklyLimitQty: weeklyLimit ?? item.weeklyLimitQty,
    scaleMax: scaleMaxNum,
    consUsedArcDash,
    consArcLen,
    barColorUsed: item.barColorUsed ?? barColorByBadge,
    badgeClass,
    showWeeklyLimitLbl: item.showWeeklyLimitLbl !== false,
    showLimitMarker: Boolean(showLimitMarker && limitMarkerX != null),
    limitMarkerX,
    limitMarkerY,
    isGas: Boolean(item.isGas),
    nums: item.nums ?? (consumptionToday != null ? `Today ${consumptionToday}` : undefined),
  };
}

/** Normalize PIH wire "Supplied vs Needed" semicircle (110 = 100% marker). */
export function normalizeSuppliedVsNeededItem(item) {
  if (!item || typeof item !== 'object') return item;

  const needQty = parseFloat(pick(item.needQty, item.requiredQty, item.needMarkerValue) ?? 110);
  const scaleMaxNum = parseFloat(
    pick(item.scaleMax, item.gaugeScaleMax, Number.isFinite(needQty) ? needQty * 1.2 : 132)
  );
  const heroNum = parseFloat(pick(item.gaugeHeroVal, item.supplied, item.value) ?? 0);

  let arcPct = parseFloat(item.gaugePct ?? item.percent);
  if (!Number.isFinite(arcPct) && scaleMaxNum > 0 && Number.isFinite(heroNum)) {
    arcPct = (heroNum / scaleMaxNum) * 100;
  }
  if (!Number.isFinite(arcPct)) arcPct = 0;

  const arc = arcDashArray(arcPct, SMALL_ARC_LEN);
  const needPct = scaleMaxNum > 0 && Number.isFinite(needQty) ? (needQty / scaleMaxNum) * 100 : 83.33;
  const needPos = item.needMarkerX != null
    ? { x: item.needMarkerX, y: item.needMarkerY }
    : arcMarkerPosition(needPct);

  return {
    ...item,
    gaugeHeroVal: pick(item.gaugeHeroVal, item.supplied, item.value) ?? heroNum,
    gaugePct: arcPct.toFixed(2),
    scaleMax: scaleMaxNum,
    consUsedArcDash: arc.dash,
    consArcLen: arc.len,
    barColorUsed: item.barColorUsed ?? '#087e8b',
    requiredLabel: item.requiredLabel ?? `${Math.round(needQty)} = 100%`,
    showNeedMarker: item.showNeedMarker !== false,
    needMarkerX: needPos.x,
    needMarkerY: needPos.y,
  };
}

function buildHeader(payload, bundle) {
  if (payload.header && typeof payload.header === 'object') return payload.header;
  if (!bundle || typeof bundle !== 'object') return null;
  return {
    bundleTitle: bundle.bundleTitle,
    bundleReportNum: bundle.bundleReportNum,
    clientTag: bundle.clientTag,
    clientLogoSrc: bundle.clientLogoSrc,
    crceLogoSrc: bundle.crceLogoSrc,
  };
}

function buildMeta(payload, bundle) {
  const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  const header = payload.header && typeof payload.header === 'object' ? payload.header : {};
  // Prefer pipeline-level length when project-level blank (avoid blank chip + duplicate labels)
  const expectedPipelineLength = pick(
    meta.expectedProjectLength,
    meta.expectedProjectLengthM,
    b.metaExpectedProjectLength,
    meta.expectedPipeLength,
    meta.expectedPipeLengthM,
    b.metaExpectedPipeLength,
    header.length,
    header.expectedPipeLength,
    header.expectedProjectLength
  );
  return {
    projectNumber: pick(
      meta.projectNumber,
      b.metaProjectNumber,
      header.projectNumber,
      header.projectId,
      payload.projectNumber,
      payload.projectId
    ),
    client: pick(
      meta.client,
      b.metaClient,
      header.client,
      header.clientName,
      header.clientTag,
      b.clientTag,
      payload.clientName,
      payload.contractor
    ),
    finalClient: pick(meta.finalClient, b.metaFinalClient, header.finalClient),
    vessel: pick(meta.vessel, b.metaVessel, header.vessel, header.siteLocationDetail),
    date: pick(meta.date, b.metaDate, header.date, payload.logDate),
    projectStart: pick(meta.projectStart, b.metaProjectStart, header.projectStart),
    expectedProjectLength: expectedPipelineLength,
    expectedProjectWelds: pick(meta.expectedProjectWelds, b.metaExpectedProjectWelds, header.expectedProjectWelds),
    manager: pick(meta.manager, b.metaManager, header.manager),
    ongoingPipe: pick(meta.ongoingPipe, b.metaOngoingPipe, header.sectionLabel, header.mainline, header.pipeNumber),
    od: pick(
      meta.od,
      b.metaOD,
      header.od,
      header.outerDiameterInch,
      header.outer_diameter_inch,
      header.odInches
    ),
    wt: pick(
      meta.wt,
      b.metaWT,
      header.wt,
      header.wallThicknessMm,
      header.wall_thickness_mm
    ),
    location: pick(meta.location, b.metaLocation, header.location, header.country),
    pipeStart: pick(meta.pipeStart, b.metaPipeStart, header.pipeStart),
    expectedPipeLength: expectedPipelineLength,
    expectedPipeWelds: pick(meta.expectedPipeWelds, b.metaExpectedPipeWelds, header.expectedPipeWelds),
    ndtType: pick(meta.ndtType, b.metaNdtType, header.ndtType),
  };
}

function buildProgress(payload, bundle) {
  const p = payload.progress && typeof payload.progress === 'object' ? payload.progress : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    pipeProgPct: pick(p.pipeProgPct, b.pipeProgPct),
    pipeArcDash: pick(p.pipeArcDash, b.pipeArcDash),
    pipeArcLen: pick(p.pipeArcLen, b.pipeArcLen),
    projProgPct: pick(p.projProgPct, b.projProgPct),
    projArcDash: pick(p.projArcDash, b.projArcDash),
    projArcLen: pick(p.projArcLen, b.projArcLen),
    progNdtAcceptedSowToDate: pick(p.progNdtAcceptedSowToDate, b.progNdtAcceptedSowToDate),
    progNdtAcceptedProjectToDate: pick(p.progNdtAcceptedProjectToDate, b.progNdtAcceptedProjectToDate),
    progressPipeFootCaption: pick(p.progressPipeFootCaption, b.progressPipeFootCaption),
    progressProjectFootCaption: pick(p.progressProjectFootCaption, b.progressProjectFootCaption),
    ndtAccBoxLabel: pick(p.ndtAccBoxLabel, b.ndtAccBoxLabel),
  };
}

function buildQuality(payload, bundle) {
  const q = payload.quality && typeof payload.quality === 'object' ? payload.quality : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    weldLogRows: Array.isArray(q.weldLogRows) ? q.weldLogRows : (Array.isArray(b.weldLogRows) ? b.weldLogRows : []),
    hasWeldLogRows: Boolean(q.hasWeldLogRows ?? b.hasWeldLogRows),
    weldLogTruncated: Boolean(q.weldLogTruncated ?? b.weldLogTruncated),
    qualCutOutStn1: pick(q.qualCutOutStn1, b.qualCutOutStn1),
    qualNdtRepair: pick(q.qualNdtRepair, b.qualNdtRepair),
    qualRepairLabel: pick(q.qualRepairLabel, b.qualRepairLabel),
    qualNdtCutOut: pick(q.qualNdtCutOut, b.qualNdtCutOut),
    pipeDefPct: pick(q.pipeDefPct, b.pipeDefPct),
    pipeDefArcDash: pick(q.pipeDefArcDash, b.pipeDefArcDash),
    pipeDefGaugeColor: pick(q.pipeDefGaugeColor, b.pipeDefGaugeColor),
    projDefPct: pick(q.projDefPct, b.projDefPct),
    projDefArcDash: pick(q.projDefArcDash, b.projDefArcDash),
    projDefGaugeColor: pick(q.projDefGaugeColor, b.projDefGaugeColor),
    pipeDefectGaugeLabel: pick(q.pipeDefectGaugeLabel, b.pipeDefectGaugeLabel),
    projectDefectGaugeLabel: pick(q.projectDefectGaugeLabel, b.projectDefectGaugeLabel),
    ndtRejectCapLabel: pick(q.ndtRejectCapLabel, b.ndtRejectCapLabel),
  };
}

function buildSafety(payload, bundle) {
  const s = payload.safety && typeof payload.safety === 'object' ? payload.safety : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    incToday: pick(s.incToday, b.safetyIncToday),
    incToDate: pick(s.incToDate, b.safetyIncToDate),
    ltiToday: pick(s.ltiToday, b.safetyLtiToday),
    ltiToDate: pick(s.ltiToDate, b.safetyLtiToDate),
    tbtToday: pick(s.tbtToday, b.safetyTbtToday),
    tbtToDate: pick(s.tbtToDate, b.safetyTbtToDate),
  };
}

function buildDowntime(payload, bundle) {
  const d = payload.downtime && typeof payload.downtime === 'object' ? payload.downtime : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  const rows = Array.isArray(d.rows)
    ? d.rows
    : (Array.isArray(b.dtRows) ? b.dtRows : []);
  return {
    rows,
    columnTotals: d.columnTotals ?? b.dtColumnTotals ?? {},
    projectToDateMin: pick(d.projectToDateMin, b.dtProjectToDateMin),
  };
}

function buildManpower(payload, bundle) {
  const m = payload.manpower && typeof payload.manpower === 'object' ? payload.manpower : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    pobDay: pick(m.pobDay, b.pobDay),
    pobNight: pick(m.pobNight, b.pobNight),
    manHoursToDate: pick(
      m.manHoursToDate,
      m.toDateManHours,
      m.manpowerToDate,
      b.manHoursToDate,
      b.toDateManHours,
      b.manpowerToDate
    ),
  };
}

function buildComments(payload, bundle) {
  const c = payload.comments && typeof payload.comments === 'object' ? payload.comments : {};
  const b = bundle && typeof bundle === 'object' ? bundle : {};
  return {
    generalDay: pick(c.generalDay, b.generalCommentsDay, b.generalComments),
    generalNight: pick(c.generalNight, b.generalCommentsNight),
    generalMerged: pick(c.generalMerged, b.generalComments),
  };
}

function buildSignatures(payload, bundle) {
  if (payload.signatures && typeof payload.signatures === 'object') return payload.signatures;
  if (!bundle || typeof bundle !== 'object') return null;
  return {
    noteManager: bundle.sigNoteManager,
    noteClient: bundle.sigNoteClient,
    noteApprover: bundle.sigNoteApprover,
    approvedBy1: {
      label: bundle.approvedBy1Label,
      name: bundle.approvedBy1Name,
      position: bundle.approvedBy1Position,
      signature: bundle.approvedBy1Signature,
    },
    approvedBy2: {
      label: bundle.approvedBy2Label,
      name: bundle.approvedBy2Name,
      position: bundle.approvedBy2Position,
      signature: bundle.approvedBy2Signature,
    },
    approvedBy3: {
      label: bundle.approvedBy3Label,
      name: bundle.approvedBy3Name,
      position: bundle.approvedBy3Position,
      signature: bundle.approvedBy3Signature,
    },
  };
}

function buildCriticalConsumables(payload, bundle) {
  const candidates = [
    payload.criticalConsumables,
    bundle?.criticalConsumables,
  ].filter((item) => item && typeof item === 'object');

  const merged = candidates.find(
    (item) =>
      item.hasData === true
      || (Array.isArray(item.leftGauges) && item.leftGauges.length > 0)
      || item.rightWire
  ) ?? candidates[0];

  if (!merged) return { hasData: false, leftGauges: [], rightWire: null };

  const leftGauges = (Array.isArray(merged.leftGauges) ? merged.leftGauges : [])
    .map(normalizeConsumableGaugeItem);

  let rightWire = merged.rightWire ?? null;
  if (rightWire && typeof rightWire === 'object') {
    rightWire = {
      ...rightWire,
      suppliedVsNeeded: rightWire.suppliedVsNeeded
        ? normalizeSuppliedVsNeededItem(rightWire.suppliedVsNeeded)
        : null,
    };
  }

  return {
    hasData: Boolean(
      merged.hasData ?? (leftGauges.length > 0 || rightWire)
    ),
    leftGauges,
    rightWire,
  };
}

/**
 * Fill blank one-page meta fields from selected project / pipeline (client fallback).
 */
export function enrichMetaFromProjectContext(meta, { project, pipeline } = {}) {
  if (!meta || typeof meta !== 'object') return meta || {};
  const next = { ...meta };
  const fill = (key, ...vals) => {
    if (next[key] != null && String(next[key]).trim() !== '') return;
    for (let i = 0; i < vals.length; i += 1) {
      const v = vals[i];
      if (v == null || v === '' || v === '—') continue;
      next[key] = v;
      return;
    }
  };
  const od = pipeline ? readSowOdDisplay(pipeline) : '—';
  const wt = pipeline ? readSowWtDisplay(pipeline) : '—';
  const length = pipeline ? displayExpectedPipeLength(pipeline) : null;

  fill('projectNumber', project?.projectId, project?.projectNumber, project?.pihRefId);
  fill('client', project?.clientName, project?.contractor);
  fill('finalClient', project?.finalClient);
  fill('manager', project?.manager);
  fill('location', project?.location);
  fill('vessel', project?.config?.siteLocationDetail, project?.siteLocationDetail);
  fill('od', od !== '—' ? od : null);
  fill('wt', wt !== '—' ? wt : null);
  fill('expectedProjectLength', length && length !== '—' ? length : null, pipeline?.expectedPipeLength);
  fill('ongoingPipe', pipeline?.pipelineSection, pipeline?.pipeStringId, pipeline?.sectionId);
  fill('ndtType', project?.ndtType, Array.isArray(project?.ndtType) ? project.ndtType.join(', ') : null);
  return next;
}

/** Fill blank HTML-preview bundle keys from project / pipeline. */
export function enrichBundleFromProjectContext(bundle, ctx = {}) {
  if (!bundle || typeof bundle !== 'object') return bundle || {};
  const meta = enrichMetaFromProjectContext(
    {
      projectNumber: bundle.metaProjectNumber,
      client: bundle.metaClient,
      finalClient: bundle.metaFinalClient,
      vessel: bundle.metaVessel,
      od: bundle.metaOD,
      wt: bundle.metaWT,
      expectedProjectLength: bundle.metaExpectedProjectLength,
      ongoingPipe: bundle.metaOngoingPipe,
      location: bundle.metaLocation,
      manager: bundle.metaManager,
      ndtType: bundle.metaNdtType,
    },
    ctx
  );
  return {
    ...bundle,
    metaProjectNumber: meta.projectNumber ?? bundle.metaProjectNumber,
    metaClient: meta.client ?? bundle.metaClient,
    metaFinalClient: meta.finalClient ?? bundle.metaFinalClient,
    metaVessel: meta.vessel ?? bundle.metaVessel,
    metaOD: meta.od ?? bundle.metaOD,
    metaWT: meta.wt ?? bundle.metaWT,
    metaExpectedProjectLength: meta.expectedProjectLength ?? bundle.metaExpectedProjectLength,
    metaOngoingPipe: meta.ongoingPipe ?? bundle.metaOngoingPipe,
    metaLocation: meta.location ?? bundle.metaLocation,
    metaManager: meta.manager ?? bundle.metaManager,
    metaNdtType: meta.ndtType ?? bundle.metaNdtType,
    clientTag: pick(bundle.clientTag, meta.client),
  };
}

/** Normalize dashboard API payload into section objects the UI expects. */
export function normalizeOnePageDashboardPayload(raw, context) {
  const payload = unwrapOnePageApiPayload(raw);
  if (!payload) return null;

  const bundle = payload.bundle && typeof payload.bundle === 'object' ? payload.bundle : null;
  const meta = enrichMetaFromProjectContext(buildMeta(payload, bundle), context);

  return {
    ...payload,
    bundle: context ? enrichBundleFromProjectContext(bundle || {}, context) : bundle,
    header: buildHeader(payload, bundle),
    meta,
    progress: buildProgress(payload, bundle),
    quality: buildQuality(payload, bundle),
    safety: buildSafety(payload, bundle),
    downtime: buildDowntime(payload, bundle),
    manpower: buildManpower(payload, bundle),
    comments: buildComments(payload, bundle),
    signatures: buildSignatures(payload, bundle),
    criticalConsumables: buildCriticalConsumables(payload, bundle),
  };
}

/**
 * Fetch one-page DPR dashboard data (structured sections, matches PDF template).
 * GET /dpr-log/one-page-dashboard?dprLogId={id}&shift={shift}
 */
export async function fetchOnePageDashboard(dprLogId, { shift = 'both', project, pipeline } = {}) {
  if (!dprLogId) throw new Error('DPR log is not loaded yet.');
  const context = { project, pipeline };
  const response = await axiosConfig.get('/dpr-log/one-page-dashboard', {
    params: { dprLogId, shift },
  });
  let normalized = normalizeOnePageDashboardPayload(response?.data ?? response, context);

  if (!hasDashboardSections(normalized)) {
    try {
      const viewPayload = await fetchOnePageReportViewRaw(dprLogId, { shift });
      const fromView = normalizeOnePageDashboardPayload(viewPayload, context);
      if (hasDashboardSections(fromView)) {
        normalized = fromView;
      }
    } catch (_) {
      /* keep primary dashboard payload */
    }
  }

  return normalized;
}

async function fetchOnePageReportViewRaw(dprLogId, { shift, sowId } = {}) {
  const params = {};
  if (shift) params.shift = shift;
  if (sowId != null && sowId !== '') params.sowId = sowId;

  const response = await axiosConfig.get(`/dpr-log/${dprLogId}/one-page-report-view`, {
    params,
  });
  return response?.data ?? response;
}

/**
 * Fetch structured one-page DPR view payload (mirrors backend PDF Handlebars bundle).
 * GET /dpr-log/{dprLogId}/one-page-report-view
 */
export async function fetchOnePageReportView(dprLogId, { shift, sowId } = {}) {
  if (!dprLogId) {
    throw new Error('DPR log is not loaded yet.');
  }
  const raw = await fetchOnePageReportViewRaw(dprLogId, { shift, sowId });
  return unwrapOnePageApiPayload(raw) ?? raw;
}

/** Normalize API shapes to { bundle, report, signatures, showSubContractor }. */
export function normalizeOnePageReportViewPayload(raw, context) {
  if (!raw || typeof raw !== 'object') {
    return { bundle: {}, report: null, signatures: null, showSubContractor: false };
  }

  let bundle = raw.bundle && typeof raw.bundle === 'object' ? raw.bundle : { ...raw };
  if (context) {
    bundle = enrichBundleFromProjectContext(bundle, context);
  }
  const report = raw.report && typeof raw.report === 'object' ? raw.report : raw.reportDetails?.reports?.onePage ?? null;
  const signatures = raw.signatures && typeof raw.signatures === 'object' ? raw.signatures : null;

  const showSubContractor = Boolean(
    raw.showSubContractor
      ?? raw.signatures?.showSubContractor
      ?? bundle.showSubContractor
      ?? (signatures?.subContractor && (signatures.subContractor.name || signatures.subContractor.signatureUrl))
  );

  return { bundle, report, signatures, showSubContractor };
}

export function displayOnePageVal(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  return String(value);
}
